import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, PhoneCall, ArrowUpRight } from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import { COMPANY } from "@/lib/constants";

const NAV_ITEMS = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/services", label: "Services", testid: "nav-services-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  if (loc.pathname.startsWith("/admin")) return null;

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-light border-b border-ink" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="container-x flex items-center justify-between h-16 sm:h-20">
        <Link to="/" data-testid="nav-logo-link">
          <LogoLockup />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-ink" : "text-muted-warm hover:text-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute inset-0 -z-10 rounded-full bg-ink/[0.06]"
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:+${COMPANY.phoneRaw}`}
            className="inline-flex items-center gap-2 text-sm text-ink font-medium hover:text-coral transition-colors"
            data-testid="nav-phone-link"
          >
            <PhoneCall className="h-4 w-4" />
            {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            className="btn-lime text-sm h-10 px-5 group"
            data-testid="nav-get-quote-btn"
          >
            Get a Quote
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </Link>
        </div>

        <button
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full bg-ink text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden border-t border-ink glass-light"
          >
            <div className="container-x py-4 flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`${item.testid}-mobile`}
                  className={({ isActive }) =>
                    `text-base font-medium py-3 px-2 ${
                      isActive ? "text-ink" : "text-muted-warm"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <a
                href={`tel:+${COMPANY.phoneRaw}`}
                className="flex items-center gap-2 text-sm text-ink py-2 px-2 font-medium"
              >
                <PhoneCall className="h-4 w-4" /> {COMPANY.phone}
              </a>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="btn-lime w-full mt-2"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
